import React, {
  useCallback,
  useEffect,
  useState,
  useMemo,
  useRef,
} from 'react';
import styled from 'styled-components';
import {
  CardPeriodType,
  OnChangePeriod,
  ParentPeriodValue,
  parsePeriodValues,
} from './utils';
import { formatDateForGraphQL, isDatePassed } from 'shared/utils/date';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import Periods from './Periods';
import {
  faCalendarDays,
  faSchool,
  faMoneyCheckDollarPen,
  faCalendarCircleUser,
} from '@fortawesome/pro-light-svg-icons';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import Selector from 'shared/design-system/components/Selector';
import { submitReport } from '../Review/api';
import { useClient } from 'shared/apollo';
import { pullReportData, useTrackEvent } from 'shared/utils/useTrackEvent';
import { useReport } from 'shared/providers/ReportProvider';
import { useUserActions } from 'shared/auth/hooks/useUserActions';

export const checkMoreThanOneYear = (answer?: string) =>
  answer === 'Takes more than a year to complete';

export const checkIsContinuously = (answer?: string) =>
  (answer || '').toLowerCase().includes('continuously');

interface PeriodRenderComponentProps {
  isThirdParty: boolean;
  reportId: number;
  reportYear?: string;
  onChange: (questionId: number, value: ParentPeriodValue | null) => unknown;
  options: string[];
  questionId: number;
  isMoreThanOneYear: boolean;
  value: ParentPeriodValue | string | null;
}

const StyledDiv = styled.div`
  width: 100%;
  text-align: center;
  margin-bottom: 24px;
  .heading {
    margin-bottom: 24px;
  }
  .cards {
    display: flex;
    justify-content: center;
    margin-bottom: 34px;
    .common-cards-wrapper {
      margin-right: 20px;
      display: flex;
      flex-direction: column;
      .common-cards {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-grow: 1;
        height: 140px;
        .calendar-year,
        .academic-year {
          align-items: center;
          justify-content: center;
          width: 196px;
          height: 100%;
        }
        .calendar-year {
          margin-right: 12px;
        }
        .academic-year {
          margin-left: 12px;
        }
        @media only screen and (max-width: 815px) {
          flex-direction: column;
          align-items: center;
          .calendar-year {
            margin-right: 0;
            margin-bottom: 6px;
          }
          .academic-year {
            margin-left: 0;
            margin-top: 6px;
          }
        }
      }
    }
    .less-common-cards-wrapper {
      margin-left: 20px;
      .less-common-cards {
        height: 140px;
        .custom-year,
        .fiscal-year {
          width: 236px;
          display: flex;
          gap: ${SPACERS.small};
          height: auto;
          .icon {
            width: 32px;
            height: 32px;
          }
          .text {
            margin-bottom: 0;
          }
        }
        .fiscal-year {
          margin-bottom: 12px;
        }
        .custom-year {
          margin-top: 12px;
          .icon {
            width: 30px;
            height: 30px;
          }
        }
      }
    }
    @media only screen and (max-width: 1050px) {
      display: flex;
      flex-direction: column;
      .common-cards-wrapper {
        margin-right: 0;
        .common-cards {
          justify-content: center;
          height: auto;
          .calendar-year,
          .academic-year {
            padding: ${SPACERS.small} 0;
            align-items: center;
            justify-content: center;
            width: 60%;
            width: 100%;
            height: 100%;
            .icon {
            }
          }
        }
      }
      .less-common-cards-wrapper {
        margin-top: 16px;
        margin-left: 0;
        .less-common-cards {
          .custom-year,
          .fiscal-year {
            width: 60%;
            margin-right: auto;
            margin-left: auto;
          }
        }
      }
    }
  }
  .annual-link {
    margin-top: ${SPACERS.large};
  }
`;

const getIsPassed = (
  endDate: Date | undefined,
  year: string | number | undefined,
): boolean => {
  if (!endDate && !year) return false;
  if (!endDate) {
    const y = typeof year === 'string' ? Number(year) : year;
    if (!y) return false;
    return y < new Date().getFullYear();
  }
  return isDatePassed(endDate);
};

const PeriodRenderComponent: React.FC<PeriodRenderComponentProps> = ({
  isThirdParty,
  onChange,
  options,
  questionId,
  reportId,
  reportYear,
  value,
  isMoreThanOneYear,
}) => {
  const client = useClient();
  const trackEvent = useTrackEvent();
  const { userStaticType } = useUserActions();
  const { report: reportState, isReportEndDatePassed } = useReport();
  const [showMoreYears, setShowMoreYears] = useState<boolean>(
    isMoreThanOneYear,
  );
  const period = useMemo(() => parsePeriodValues(value, options), [
    value,
    options,
  ]);
  const isFirst = useRef<boolean>(true);
  const isPassed = useRef<boolean>(getIsPassed(period.endDate, reportYear));
  const mainCardRef = useRef<HTMLDivElement>(null);
  const periodRef = useRef<HTMLDivElement>(null);
  const [startDate, setStartDate] = useState<Date | undefined>(
    period.startDate,
  );
  const [endDate, setEndDate] = useState<Date | undefined>(period.endDate);
  const [selectedCard, setSelectedCard] = useState<CardPeriodType | undefined>(
    period.card || (isMoreThanOneYear ? 'customYear' : undefined),
  );

  useEffect(() => {
    if (isFirst.current) {
      isFirst.current = false;
      return;
    }
    const passed = getIsPassed(endDate, reportYear);
    if (passed !== isPassed.current) {
      isPassed.current = passed;
      const nextStage = passed ? 'FINAL' : 'INITIAL';
      const previousStage = passed ? 'INITIAL' : 'FINAL';
      submitReport(client!, reportId, 'Draft', nextStage).then(() => {
        const reportData =
          reportState.type === 'REQUEST_SUCCESS' ? reportState.data : undefined;
        trackEvent('ReportStatusChanged', {
          ...(reportData
            ? pullReportData(reportData, isReportEndDatePassed, userStaticType)
            : {}),
          reportId,
          previousStatus: 'Draft',
          previousStage,
          status: 'Draft',
          stage: nextStage,
        });
      });
    }
  }, [endDate]);

  // Set initial value for edition on warnings
  useEffect(() => {
    if (
      startDate &&
      endDate &&
      value &&
      Array.isArray(value) &&
      value.includes('CustomYear')
    ) {
      const startFixed = new Date(
        startDate.getTime() - startDate.getTimezoneOffset() * 60 * 1000,
      );
      const endFixed = new Date(
        endDate.getTime() - endDate.getTimezoneOffset() * 60 * 1000,
      );
      const newValue = {
        option: 'CustomYear',
        startDate: startFixed.toISOString().split('T')[0],
        endDate: endFixed.toISOString().split('T')[0],
      };
      onChange(questionId, newValue);
    }
  }, []);

  useEffect(() => {
    const { startDate, endDate } = parsePeriodValues(value, options);
    setStartDate(startDate);
    setEndDate(endDate);
  }, [value, setStartDate, setEndDate]);

  const handleChange: OnChangePeriod = useCallback(
    value => {
      if (!value.startDate || !value.endDate) {
        onChange(questionId, null);
        return;
      }
      onChange(questionId, {
        option: value.option,
        startDate: formatDateForGraphQL(value.startDate),
        endDate: formatDateForGraphQL(value.endDate),
      });
      periodRef.current?.scrollIntoView?.();
    },
    [onChange, periodRef],
  );
  const handleClickMainCards = useCallback(
    (card: CardPeriodType | undefined) => {
      setSelectedCard(card);
      // https://caniuse.com/?search=scrollIntoView
      // It may not exist in some browsers
      mainCardRef.current?.scrollIntoView?.();
    },
    [mainCardRef, setSelectedCard],
  );

  const handleSelectCard = useCallback(
    (cardType?: CardPeriodType, newStart?: Date, newEnd?: Date) => {
      setStartDate(newStart);
      setEndDate(newEnd);
      setSelectedCard(cardType);
    },
    [setSelectedCard, setStartDate, setEndDate],
  );
  return (
    <StyledDiv ref={mainCardRef}>
      {!showMoreYears && (
        <TrueImpactText variant="heading" className="heading">
          Year Type
        </TrueImpactText>
      )}
      {!showMoreYears && (
        <div className="cards">
          <div className="common-cards-wrapper">
            <TrueImpactText variant="heading-subheading">
              Most Common
            </TrueImpactText>
            <div className="common-cards">
              <Selector
                id="calendarYear"
                onClick={() => handleClickMainCards('calendarYear')}
                icon={faCalendarDays}
                selected={selectedCard === 'calendarYear'}
                className="calendar-year"
                label="Calendar Year"
                variant="primary-radio"
              />
              <Selector
                id="academicYear"
                onClick={() => handleClickMainCards('academicYear')}
                icon={faSchool}
                selected={selectedCard === 'academicYear'}
                className="academic-year"
                label="Academic Year"
                variant="primary-radio"
              />
            </div>
          </div>
          <div className="less-common-cards-wrapper">
            <TrueImpactText variant="heading-subheading">
              Less Common
            </TrueImpactText>
            <div className="less-common-cards">
              <Selector
                id="fiscalYear"
                onClick={() => handleClickMainCards('fiscalYear')}
                icon={faMoneyCheckDollarPen}
                selected={selectedCard === 'fiscalYear'}
                className="fiscal-year"
                label="Fiscal Year"
                variant="radio"
              />
              <Selector
                id="custom"
                onClick={() => handleClickMainCards('customYear')}
                icon={faCalendarCircleUser}
                selected={selectedCard === 'customYear'}
                className="custom-year"
                label="Custom"
                variant="radio"
              />
            </div>
          </div>
        </div>
      )}
      <div ref={periodRef}>
        <Periods
          reportId={reportId}
          periodType={selectedCard}
          startDate={startDate}
          endDate={endDate}
          reportYear={reportYear}
          isThirdParty={isThirdParty}
          showMoreYears={showMoreYears}
          onChangePeriod={handleChange}
          setSelectedCard={handleSelectCard}
        />
      </div>
      {showMoreYears && (
        <TrueImpactText
          color="ctaPrimary"
          className="annual-link"
          onClick={() => setShowMoreYears(false)}
        >
          I need to select an annual reporting period.
        </TrueImpactText>
      )}
    </StyledDiv>
  );
};

export default React.memo(PeriodRenderComponent);
